import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface PendingOrder {
  id: string;
  supplier?: string;
  orderDate: string;
  expectedDelivery: string;
  status: string;
  lineCount: number;
  orderedTotal: string;
}

interface Line {
  itemId: string;
  itemName: string;
  orderedQuantity: number;
  receivedQuantity: number;
  quantityGap: number;
  quotedPrice: string;
  chargedPrice: string;
  priceGap: string;
  priceChanged: boolean;
  valueGap: string;
  flagged: boolean;
}

interface Comparison {
  orderId: string;
  supplier?: string;
  invoiceNumber?: string;
  deliveryNoteNumber?: string;
  reconciledAt?: string;
  checkedIn: boolean;
  reconcileNote?: string;
  lines: Line[];
  orderedTotal: string;
  chargedTotal: string;
  difference: string;
  overcharged: boolean;
  flaggedCount: number;
  invoiceMismatch?: string | null;
}

/**
 * Checking a Sunrich delivery against what was ordered.
 *
 * The point is the price column: a supplier raising a price quietly is the
 * thing a plain "received" tick never catches.
 */
export default function DeliveriesPanel({ isManager }: { isManager: boolean }) {
  const [pending, setPending] = useState<PendingOrder[]>([]);
  const [open, setOpen] = useState<Comparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [invoiceNo, setInvoiceNo] = useState('');
  const [noteNo, setNoteNo] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [note, setNote] = useState('');
  const [entry, setEntry] = useState<Record<string, { qty: string; price: string }>>({});

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 5000);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const loadPending = async () => {
    try {
      const res = await apiClient.pendingDeliveries();
      setPending(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      fail(e, 'Could not load deliveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const openOrder = async (id: string) => {
    setBusy(true);
    try {
      const res = await apiClient.compareDelivery(id);
      const c: Comparison = res.data;
      setOpen(c);
      setInvoiceNo(c.invoiceNumber || '');
      setNoteNo(c.deliveryNoteNumber || '');
      setInvoiceTotal('');
      setNote('');
      // Start from what was ordered — most lines arrive as asked
      const seed: Record<string, { qty: string; price: string }> = {};
      for (const l of c.lines) {
        seed[l.itemId] = { qty: String(l.orderedQuantity), price: l.quotedPrice };
      }
      setEntry(seed);
      setErr('');
    } catch (e: any) {
      fail(e, 'Could not open that order');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!open) return;
    if (!invoiceNo.trim() && !noteNo.trim()) {
      setErr('Record the invoice or delivery note number so this can be traced back.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.reconcileDelivery(open.orderId, {
        invoiceNumber: invoiceNo.trim() || undefined,
        deliveryNoteNumber: noteNo.trim() || undefined,
        invoicedTotal: invoiceTotal.trim() || undefined,
        note: note.trim() || undefined,
        lines: open.lines.map((l) => ({
          itemId: l.itemId,
          receivedQuantity: Number(entry[l.itemId]?.qty ?? l.orderedQuantity),
          chargedPrice: entry[l.itemId]?.price,
        })),
      });
      const c: Comparison = res.data;
      setOpen(c);
      flash(
        c.flaggedCount === 0
          ? 'Checked in — everything matched the order.'
          : `Checked in — ${c.flaggedCount} line${c.flaggedCount === 1 ? '' : 's'} differ from the order.`,
      );
      await loadPending();
    } catch (e: any) {
      fail(e, 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Totals from what is currently typed in, not from the server. Before a
   * delivery is checked in every received quantity is zero, so the stored
   * figures would show a shortfall nobody has counted.
   */
  const live = (() => {
    if (!open) return null;
    let ordered = 0;
    let charged = 0;
    let flagged = 0;
    for (const l of open.lines) {
      const e = entry[l.itemId] || { qty: '0', price: l.quotedPrice };
      const qty = Number(e.qty) || 0;
      const price = Number(e.price) || 0;
      ordered += Number(l.quotedPrice) * l.orderedQuantity;
      charged += price * qty;
      if (qty !== l.orderedQuantity || price !== Number(l.quotedPrice)) flagged += 1;
    }
    return { ordered, charged, difference: charged - ordered, flagged };
  })();

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const field =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      {!open ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Deliveries to check</h3>
            <p className="text-sm text-gray-500">
              Orders placed but not yet checked against an invoice.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {pending.length === 0 ? (
                  <tr>
                    <td className="py-12 text-center text-gray-500">
                      Nothing waiting. Every delivery has been checked in.
                    </td>
                  </tr>
                ) : (
                  pending.map((o) => (
                    <tr key={o.id} className="border-b last:border-0 hover:bg-amber-50">
                      <td className="py-3 px-6">
                        <p className="font-medium text-gray-900">{o.supplier || 'Supplier'}</p>
                        <p className="text-xs text-gray-500">
                          ordered {new Date(o.orderDate).toLocaleDateString('en-ZA')} · {o.lineCount} lines
                        </p>
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold text-gray-900">
                        R {Number(o.orderedTotal).toFixed(2)}
                      </td>
                      <td className="py-3 px-6 text-right">
                        <button
                          onClick={() => openOrder(o.id)}
                          disabled={busy}
                          className="text-sm bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded font-medium"
                        >
                          Check in
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{open.supplier} delivery</h3>
              <p className="text-sm text-gray-500">
                {open.checkedIn
                  ? 'Already checked in. Change anything that was recorded wrong.'
                  : 'Type what actually arrived and what you were charged. Figures update as you go.'}
              </p>
            </div>
            <button
              onClick={() => {
                setOpen(null);
                loadPending();
              }}
              className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
            >
              Back to the list
            </button>
          </div>

          {/* Where the money landed */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ordered</p>
              <p className="text-2xl font-bold tabular-nums text-gray-900">R {live!.ordered.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Charged</p>
              <p className="text-2xl font-bold tabular-nums text-gray-900">R {live!.charged.toFixed(2)}</p>
            </div>
            <div
              className={`rounded-lg shadow p-4 ${
                live!.difference === 0
                  ? 'bg-white'
                  : live!.difference > 0
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-green-50 border border-green-200'
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Difference</p>
              <p
                className={`text-2xl font-bold tabular-nums ${
                  live!.difference === 0
                    ? 'text-gray-900'
                    : live!.difference > 0
                      ? 'text-red-700'
                      : 'text-green-700'
                }`}
              >
                {live!.difference > 0 ? '+' : ''}R {live!.difference.toFixed(2)}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {live!.difference === 0
                  ? 'matches the order'
                  : live!.difference > 0
                    ? 'more than you ordered'
                    : 'less than you ordered'}
              </p>
            </div>
          </div>

          {open.invoiceMismatch && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm">
              The invoice total is R {open.invoiceMismatch} away from what these lines add up to. Worth
              querying before it is paid.
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="py-2.5 px-5 text-left font-semibold">Item</th>
                    <th className="py-2.5 px-3 text-right font-semibold">Ordered</th>
                    <th className="py-2.5 px-3 text-right font-semibold">Came</th>
                    <th className="py-2.5 px-3 text-right font-semibold">Quoted</th>
                    <th className="py-2.5 px-3 text-right font-semibold">Charged</th>
                    <th className="py-2.5 px-5 text-right font-semibold">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {open.lines.map((l) => {
                    const e = entry[l.itemId] || { qty: '', price: '' };
                    const qtyOff = Number(e.qty) !== l.orderedQuantity;
                    const priceOff = Number(e.price) !== Number(l.quotedPrice);
                    const lineGap =
                      (Number(e.price) || 0) * (Number(e.qty) || 0) -
                      Number(l.quotedPrice) * l.orderedQuantity;
                    return (
                      <tr key={l.itemId} className="border-b last:border-0">
                        <td className="py-2 px-5 font-medium text-gray-900">{l.itemName}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-500">{l.orderedQuantity}</td>
                        <td className="py-2 px-3 w-24">
                          <input
                            value={e.qty}
                            onChange={(ev) =>
                              setEntry({ ...entry, [l.itemId]: { ...e, qty: ev.target.value } })
                            }
                            inputMode="numeric"
                            className={`w-full px-2 py-1 border rounded text-right tabular-nums ${
                              qtyOff ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
                            }`}
                          />
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-500">{l.quotedPrice}</td>
                        <td className="py-2 px-3 w-24">
                          <input
                            value={e.price}
                            onChange={(ev) =>
                              setEntry({ ...entry, [l.itemId]: { ...e, price: ev.target.value } })
                            }
                            inputMode="decimal"
                            className={`w-full px-2 py-1 border rounded text-right tabular-nums ${
                              priceOff ? 'border-red-400 bg-red-50' : 'border-gray-300'
                            }`}
                          />
                        </td>
                        <td
                          className={`py-2 px-5 text-right tabular-nums font-semibold ${
                            lineGap === 0 ? 'text-gray-400' : lineGap > 0 ? 'text-red-700' : 'text-green-700'
                          }`}
                        >
                          {lineGap > 0 ? '+' : ''}
                          {lineGap.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="font-medium text-gray-900">The paperwork</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Invoice number" className={field} />
              <input value={noteNo} onChange={(e) => setNoteNo(e.target.value)} placeholder="Delivery note number" className={field} />
              <input value={invoiceTotal} onChange={(e) => setInvoiceTotal(e.target.value)} placeholder="Invoice total (optional)" inputMode="decimal" className={`${field} tabular-nums`} />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth noting" className={field} />
            </div>
            {isManager ? (
              <button
                onClick={submit}
                disabled={busy}
                className="mt-4 w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-lg"
              >
                {open.reconciledAt ? 'Update the check-in' : 'Sign off this delivery'}
              </button>
            ) : (
              <p className="mt-4 text-sm text-gray-500">A manager signs off the delivery.</p>
            )}
            {open.reconciledAt && (
              <p className="mt-3 text-xs text-gray-500">
                Checked in {new Date(open.reconciledAt).toLocaleString('en-ZA')}
                {open.reconcileNote ? ` · ${open.reconcileNote}` : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
